export type DiagnosticOrigin = "parser" | "validation";
export type DiagnosticSeverity = "error" | "warning";

export type DiagnosticLocation = {
    line: number;
    column?: number;
    startOffset?: number;
    endOffset?: number;
};

export type Diagnostic = {
    // Stable identity for de-duplication + React keys.
    // `${origin}:${type}@${line}:${column}#${loopIndex ?? ""}`.
    // `loopIndex` is part of the identity because the same source location can
    // raise distinct errors across different loop iterations (e.g. inside a `map`).
    id: string;
    origin: DiagnosticOrigin;
    type: string;
    severity: DiagnosticSeverity;
    message: string;
    cause?: string;
    location: DiagnosticLocation;
    loopIndex?: number;
    snippet?: string;
    // Populated by validation diagnostics; identifies the offending component.
    componentName?: string;
};

export type DiagnosticBucket = {
    fileName?: string;
    // The template source — kept so consumers (UI + LSP) can highlight the offending region.
    source: string;
    // Identity of the parse pass that produced these diagnostics (=== the source content).
    // A change signals a fresh parse, which clears stale diagnostics.
    parseId: string;
    diagnostics: Diagnostic[];
};

export type DiagnosticsState = {
    byTemplate: Record<string, DiagnosticBucket>;
};

export const REPORT_DIAGNOSTIC = "REPORT_DIAGNOSTIC";
export const FINALIZE_PARSE = "FINALIZE_PARSE";
export const CLEAR_DIAGNOSTICS = "CLEAR_DIAGNOSTICS";
export const CLEAR_DIAGNOSTICS_FOR_FILE = "CLEAR_DIAGNOSTICS_FOR_FILE";

export interface ReportDiagnosticAction {
    type: typeof REPORT_DIAGNOSTIC;
    bucketKey: string;
    fileName?: string;
    source: string;
    parseId: string;
    diagnostic: Diagnostic;
}

export interface FinalizeParseAction {
    type: typeof FINALIZE_PARSE;
    bucketKey: string;
    parseId: string;
}

export interface ClearDiagnosticsAction {
    type: typeof CLEAR_DIAGNOSTICS;
}

export interface ClearDiagnosticsForFileAction {
    type: typeof CLEAR_DIAGNOSTICS_FOR_FILE;
    fileName: string;
    diagnosticId?: string;
}

export type DiagnosticsActionTypes =
    | ReportDiagnosticAction
    | FinalizeParseAction
    | ClearDiagnosticsAction
    | ClearDiagnosticsForFileAction;
